import { BadgeMultiSelect } from '@dealstack/frontend/app/pages/Valuations/components/BadgeMultiSelect/BadgeMultiSelect';
import { BreakdownAction } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownAction';
import { BreakdownPopover } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownPopover';
import { BreakdownItem } from '@dealstack/frontend/app/pages/Valuations/components/BreakdownPopover/BreakdownPopover.interface';
import { CopyAction } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/CopyAction';
import { DefaultHeaderCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/DefaultHeaderCell';
import { NewDefaultCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/NewDefaultCell';
import { NewEntityCell } from '@dealstack/frontend/app/pages/Valuations/components/table/cells/NewEntityCell';
import { TableColumnBuilder } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder';
import { ColumnBuilderConfig } from '@dealstack/frontend/app/pages/Valuations/components/table/columnBuilder/columnBuilder.types';
import { isDisplayValueValid } from '@dealstack/frontend/app/pages/Valuations/components/table/utils/displayValue';
import {
	formatMoicMetric,
	formatPercentage,
	formatSubColumnValue,
	formatSummaryDecimal,
} from '@dealstack/frontend/app/pages/Valuations/components/table/utils/formatting';
import {
	ClassifierOption,
	getEntityClassifierBadgeConfig,
} from '@dealstack/frontend/app/pages/Valuations/shared/entityClassiferBadgeConfig';
import {
	useTranslationCapManage,
	useTranslationValuations,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { I18Namespaces } from '@dealstack/shared/lib/common/i18n/i18n';
import { HighPrecisionDecimal } from '@dealstack/shared/lib/format/currency/highPrecisionDecimal';
import { LegalPersonTags } from '@dealstack/shared/lib/generated';
import { intersectionBy } from 'lodash';
import { useMemo } from 'react';
import { TranslateFunction } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.types';
import type {
	CreateByPersonTableColumnsParams,
	NewByPersonValuationRow,
} from './ByPersonValuationTable.utils.types';
import { ColumnDef, Row } from '@tanstack/react-table';

export const tagsMultiselectFilter = (
	row: Row<NewByPersonValuationRow>,
	_: string,
	filterValues: ClassifierOption[]
) => {
	if (!filterValues || filterValues.length === 0) {
		return true;
	}

	const rowClassifiers = row.original.metadata?.classifiers ?? [];
	if (intersectionBy(rowClassifiers, filterValues, 'value').length > 0) {
		return true;
	}

	// Also check sub-row classifiers (e.g. group tags on expandable rows)
	const subRows = row.original.subRows ?? [];
	return subRows.some((subRow) => {
		const subClassifiers = subRow.metadata?.classifiers ?? [];
		return intersectionBy(subClassifiers, filterValues, 'value').length > 0;
	});
};

tagsMultiselectFilter.autoRemove = (val: ClassifierOption[]) => !val || val.length === 0;

// Builds the items for the Net Proceeds breakdown popover.
// Two aggregate lines: total proceeds (positive) and total outflows (negated).
// Sums to net proceeds. We deliberately don't pass `units` so the popover
// skips its "× per-unit price" rendering — these are aggregates, not securities.
function buildNetProceedsItems(
	row: NewByPersonValuationRow,
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>
): BreakdownItem[] {
	const items: BreakdownItem[] = [];

	if (!row.proceeds.isZero()) {
		items.push({
			name: tValuations('summary.by_person.total_proceeds_header'),
			amount: row.proceeds,
		});
	}
	if (!row.outflows.isZero()) {
		items.push({
			name: tValuations('summary.by_person.total_outflows_header'),
			amount: row.outflows.negated(),
		});
	}

	return items;
}

export function createByPersonTableColumns(
	params: CreateByPersonTableColumnsParams,
	tValuations: TranslateFunction<I18Namespaces.VALUATIONS>,
	tCapManage: TranslateFunction<I18Namespaces.CAP_MANAGE>
): ColumnDef<NewByPersonValuationRow>[] {
	const config: ColumnBuilderConfig<NewByPersonValuationRow> = {
		showFilterInputs: params.showFilterInputs,
		columnFilters: params.columnFilters,
		updateColumnFilter: params.updateColumnFilter,
		toggleSubColumns: params.toggleSubColumns,
		hasVisibleSubColumns: params.hasVisibleSubColumns,
		isLastSubColumn: params.isLastSubColumn,
		components: {
			HeaderCell: DefaultHeaderCell,
			DefaultCell: NewDefaultCell,
		},
		defaultRenderActions: (_row, value, isHovered, formatter) => {
			const data = formatter ? formatter(value) : value;
			if (!isDisplayValueValid(data)) {
				return null;
			}
			const text = typeof data === 'string' ? data : String(data);
			return <CopyAction text={text} isHovered={isHovered} />;
		},
	};

	const builder = new TableColumnBuilder<NewByPersonValuationRow>(config);

	const defaultColumns = builder
		.addColumnWithCustomCell({
			id: 'person',
			header: tValuations('summary.by_person.person_header'),
			accessor: (row) => row.person,
			cellComponent: NewEntityCell,
			cellPropsMapper: ({ value, tanstackRow, isLastRow, isLastColumn, columnIndex }) => ({
				value: value as string | number | null,
				row: tanstackRow,
				isLastRow,
				isLastColumn,
				isExpanded: tanstackRow.getIsExpanded(),
				columnIndex,
			}),
			filterComponent: (() => {
				const currentFilter = params.columnFilters.find((filter) => filter.id === 'person');
				const legalPersonTags: ClassifierOption[] = Object.values(LegalPersonTags).map(
					(tag) => {
						const { label, iconVariant } = getEntityClassifierBadgeConfig(
							tag,
							tCapManage
						);
						return {
							label,
							value: tag,
							iconVariant,
						};
					}
				);
				const tagOptions = [...params.classifierOptions, ...legalPersonTags];

				return (
					<BadgeMultiSelect
						options={tagOptions}
						selectedValues={(currentFilter?.value ?? []) as ClassifierOption[]}
						onSelectionChange={(selectedValues: ClassifierOption[]) =>
							params.updateColumnFilter('person', selectedValues)
						}
						placeholder={tValuations('summary.by_person.classifier_filter_placeholder')}
						width="100%"
					/>
				);
			})(),
			filterFn: tagsMultiselectFilter,
		})
		.addColumnWithSubColumns({
			id: 'proceeds',
			header: tValuations('summary.by_person.total_proceeds_header'),
			accessor: (row) => row.proceeds,
			alignment: 'end',
			enableExpanding: true,
			formatter: formatSummaryDecimal,
			renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
				const breakdown = row.metadata?.securitiesBreakdown ?? [];
				const items: BreakdownItem[] = breakdown.map((s) => ({
					name: s.name,
					units: new HighPrecisionDecimal(s.units),
					amount: new HighPrecisionDecimal(s.amount),
					issuerId: s.issuerId,
				}));
				if (!row.receivables.isZero()) {
					items.push({
						name: tValuations('summary.by_person.receivables_header'),
						amount: row.receivables,
					});
				}
				if (!row.cashOnBalanceSheet.isZero()) {
					items.push({
						name: tValuations('summary.by_person.cash_on_balance_sheet_header'),
						amount: row.cashOnBalanceSheet,
					});
				}
				if (items.length === 0) {
					return null;
				}
				return (
					<BreakdownAction
						isVisible={isHovered}
						renderContent={(close) => (
							<BreakdownPopover
								items={items}
								showSecurityName
								showTotal
								currency={params.currency}
								onClose={close}
							/>
						)}
					/>
				);
			},
			subColumns: [
				{
					id: 'securitiesHeld',
					header: tValuations('summary.by_person.securities_held_header'),
					accessor: (row) => row.securitiesHeld,
					alignment: 'end',
					formatter: formatSubColumnValue,
					renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
						const breakdown = row.metadata?.securitiesBreakdown ?? [];
						if (breakdown.length === 0) {
							return null;
						}
						return (
							<BreakdownAction
								isVisible={isHovered}
								renderContent={(close) => (
									<BreakdownPopover
										items={breakdown.map((s) => ({
											name: s.name,
											units: new HighPrecisionDecimal(s.units),
											amount: new HighPrecisionDecimal(s.amount),
											issuerId: s.issuerId,
										}))}
										showSecurityName
										currency={params.currency}
										onClose={close}
									/>
								)}
							/>
						);
					},
				},
				{
					id: 'receivables',
					header: tValuations('summary.by_person.receivables_header'),
					accessor: (row) => row.receivables,
					alignment: 'end',
					formatter: formatSubColumnValue,
				},
				{
					id: 'cashOnBalanceSheet',
					header: tValuations('summary.by_person.cash_on_balance_sheet_header'),
					accessor: (row) => row.cashOnBalanceSheet,
					alignment: 'end',
					formatter: formatSubColumnValue,
				},
			],
			isLastColumn: false,
		})
		.addColumnWithSubColumns({
			id: 'outflows',
			header: tValuations('summary.by_person.total_outflows_header'),
			accessor: (row) => row.outflows,
			alignment: 'end',
			enableExpanding: true,
			formatter: formatSummaryDecimal,
			renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
				const breakdown = row.metadata?.outflowsSecuritiesBreakdown ?? [];
				const items: BreakdownItem[] = breakdown.map((s) => ({
					name: s.name,
					units: new HighPrecisionDecimal(s.units),
					amount: new HighPrecisionDecimal(s.amount),
					issuerId: s.issuerId,
				}));
				if (!row.payables.isZero()) {
					items.push({
						name: tValuations('summary.by_person.payables_header'),
						amount: row.payables,
					});
				}
				if (items.length === 0) {
					return null;
				}
				return (
					<BreakdownAction
						isVisible={isHovered}
						renderContent={(close) => (
							<BreakdownPopover
								items={items}
								showSecurityName
								showTotal
								currency={params.currency}
								onClose={close}
							/>
						)}
					/>
				);
			},
			subColumns: [
				{
					id: 'securitiesIssued',
					header: tValuations('summary.by_person.securities_issued_header'),
					accessor: (row) => row.securitiesIssued,
					alignment: 'end',
					formatter: formatSubColumnValue,
					renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
						const breakdown = row.metadata?.outflowsSecuritiesBreakdown ?? [];
						if (breakdown.length === 0) {
							return null;
						}
						return (
							<BreakdownAction
								isVisible={isHovered}
								renderContent={(close) => (
									<BreakdownPopover
										items={breakdown.map((s) => ({
											name: s.name,
											units: new HighPrecisionDecimal(s.units),
											amount: new HighPrecisionDecimal(s.amount),
											issuerId: s.issuerId,
										}))}
										showSecurityName
										currency={params.currency}
										onClose={close}
									/>
								)}
							/>
						);
					},
				},
				{
					id: 'payables',
					header: tValuations('summary.by_person.payables_header'),
					accessor: (row) => row.payables,
					alignment: 'end',
					formatter: formatSubColumnValue,
				},
			],
			isLastColumn: false,
		})
		.addDefaultColumn({
			id: 'netProceeds',
			header: tValuations('summary.by_person.net_proceeds_header'),
			accessor: (row) => row.netProceeds,
			alignment: 'end',
			formatter: formatSummaryDecimal,
			renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
				const items = buildNetProceedsItems(row, tValuations);
				if (items.length === 0) {
					return null;
				}
				return (
					<BreakdownAction
						isVisible={isHovered}
						renderContent={(close) => (
							<BreakdownPopover
								items={items}
								showSecurityName
								showTotal
								currency={params.currency}
								onClose={close}
							/>
						)}
					/>
				);
			},
		})
		.addDefaultColumn({
			id: 'totalNetProceedsPercent',
			header: tValuations('summary.by_person.total_net_proceeds_percent_header'),
			accessor: (row) => row.totalNetProceedsPercent,
			alignment: 'end',
			formatter: formatPercentage,
			renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
				const totalNetProceeds = row.metadata?.totalNetProceeds;
				if (!totalNetProceeds || totalNetProceeds.isZero()) {
					return null;
				}
				return (
					<BreakdownAction
						isVisible={isHovered}
						renderContent={(close) => (
							<BreakdownPopover
								items={[
									{
										name: tValuations('summary.by_person.net_proceeds_header'),
										amount: row.netProceeds,
									},
									{
										name: tValuations(
											'summary.by_person.total_net_proceeds_header'
										),
										amount: totalNetProceeds,
									},
								]}
								showSecurityName
								showTotal
								totalText={formatPercentage(row.totalNetProceedsPercent)}
								onClose={close}
							/>
						)}
					/>
				);
			},
		})
		.addDefaultColumn({
			id: 'moneyIn',
			header: tValuations('summary.by_person.money_in_header'),
			accessor: (row) => row.moneyIn,
			alignment: 'end',
			formatter: formatSummaryDecimal,
		})
		.addDefaultColumn({
			id: 'moneyOut',
			header: tValuations('summary.by_person.money_out_header'),
			accessor: (row) => row.moneyOut,
			alignment: 'end',
			formatter: formatSummaryDecimal,
		})
		.addDefaultColumn({
			id: 'moic',
			header: tValuations('summary.by_person.moic_header'),
			accessor: (row) => row.moic,
			alignment: 'end',
			formatter: formatMoicMetric,
			renderActions: (row: NewByPersonValuationRow, _value, isHovered) => {
				const moic = row.moic;
				if (!moic || moic.isZero()) {
					return null;
				}

				const items: BreakdownItem[] = [
					{
						name: tValuations('summary.by_person.total_proceeds_header'),
						amount: row.proceeds,
					},
				];
				if (row.moneyOut) {
					items.push({
						name: tValuations('summary.by_person.money_out_header'),
						amount: row.moneyOut,
					});
				}
				if (row.moneyIn) {
					items.push({
						name: tValuations('summary.by_person.money_in_header'),
						amount: row.moneyIn,
					});
				}

				return (
					<BreakdownAction
						isVisible={isHovered}
						renderContent={(close) => (
							<BreakdownPopover
								items={items}
								showSecurityName
								showTotal
								totalText={formatMoicMetric(moic)}
								onClose={close}
							/>
						)}
					/>
				);
			},
		})
		.addDefaultColumn({
			id: 'xirr',
			header: tValuations('summary.by_person.xirr_header'),
			accessor: (row) => row.xirr,
			alignment: 'end',
			isLastColumn: true,
			formatter: formatPercentage,
		});

	return defaultColumns.build();
}

export function useByPersonValuationTableColumns({
	showFilterInputs,
	columnFilters,
	updateColumnFilter,
	toggleSubColumns,
	hasVisibleSubColumns,
	isLastSubColumn,
	classifierOptions,
	currency,
}: CreateByPersonTableColumnsParams): ColumnDef<NewByPersonValuationRow>[] {
	const tValuations = useTranslationValuations();
	const tCapManage = useTranslationCapManage();

	const columns = useMemo(() => {
		const columns = createByPersonTableColumns(
			{
				showFilterInputs,
				columnFilters,
				updateColumnFilter,
				toggleSubColumns,
				hasVisibleSubColumns,
				isLastSubColumn,
				classifierOptions,
				currency,
			},
			tValuations,
			tCapManage
		);

		return columns;
	}, [
		showFilterInputs,
		columnFilters,
		updateColumnFilter,
		toggleSubColumns,
		hasVisibleSubColumns,
		isLastSubColumn,
		classifierOptions,
		currency,
		tValuations,
		tCapManage,
	]);

	return columns;
}
